import { useTranslate, type Identifier } from "ra-core";
import { useCallback, useMemo, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router";

import { PillGroup } from "@/components/market-intel/components";
import { SegmentedControl } from "@/components/atomic-crm/tasks/SegmentedControl";
import { useIsMobile } from "@/hooks/use-mobile";

import "@/styles/market-intel.css";
import "@/styles/marketing.css";

import { AnalyticsView } from "./AnalyticsView";
import { CalendarView } from "./CalendarView";
import { ChannelsView } from "./ChannelsView";
import { MarketingContext, type MarketingActions } from "./context";
import type { MetricsTarget, PieceFormValues } from "./forms";
import { findById, useMarketingData, useSales } from "./hooks";
import { MetricsForm } from "./MetricsForm";
import { PieceForm } from "./PieceForm";
import { PieceSheet } from "./PieceSheet";
import { PipelineView } from "./PipelineView";
import { PlanDetail, PlansView } from "./PlansView";
import {
  MARKETING_BASE,
  MARKETING_TABS,
  marketingTabOfPath,
  marketingTabPath,
  PIECE_PARAM,
  withPiece,
  type MarketingTab,
} from "./routes";
import { SummaryView } from "./SummaryView";
import type { ContentItem, ContentMetric } from "./types";

/**
 * "Marketing": Litus' own content (calendar, pipeline, plans), channels and
 * results, under /marketing/*. Same chrome as Competencia: a sticky glass
 * pill row under the top bar on a phone (six sections scroll sideways), a
 * segmented control next to the title on a desktop. Any piece opens in a
 * sheet on top of the current section (?pieza=ID).
 */
export const MarketingPage = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const tab = marketingTabOfPath(pathname);
  const { data, isPending, error } = useMarketingData();
  const sales = useSales();

  const [pieceForm, setPieceForm] = useState<{
    open: boolean;
    piece: ContentItem | null;
    prefill?: Partial<PieceFormValues>;
  }>({ open: false, piece: null });
  const [metricsForm, setMetricsForm] = useState<{
    open: boolean;
    target?: MetricsTarget;
    row?: ContentMetric;
  }>({ open: false });

  const openPiece = useCallback(
    (id: Identifier) => navigate({ search: withPiece(search, id) }),
    [navigate, search],
  );
  const closePiece = () =>
    navigate({ search: withPiece(search, null) }, { replace: true });

  const actions = useMemo<MarketingActions>(
    () => ({
      data,
      sales,
      openPiece,
      newPiece: (prefill) => setPieceForm({ open: true, piece: null, prefill }),
      editPiece: (piece) => setPieceForm({ open: true, piece }),
      recordMetrics: (target, row) =>
        setMetricsForm({ open: true, target, row }),
    }),
    [data, sales, openPiece],
  );

  const pieceId = params.get(PIECE_PARAM);
  const openedPiece = pieceId ? findById(data.items, pieceId) : undefined;

  const options = MARKETING_TABS.map((entry) => ({
    value: entry.id,
    label: translate(`marketing.tabs.${entry.id}`),
  }));
  const onChange = (next: MarketingTab) => navigate(marketingTabPath(next));
  const label = translate("marketing.tabs.label");

  return (
    <MarketingContext.Provider value={actions}>
      <div
        className={
          isMobile
            ? "overflow-x-clip px-4"
            : "mx-auto w-full max-w-[1240px] pt-2"
        }
      >
        <header
          className={
            isMobile
              ? "pb-2"
              : "mb-5 flex flex-wrap items-end justify-between gap-4"
          }
        >
          <h1
            className={
              isMobile
                ? "text-2xl leading-tight font-bold tracking-tight"
                : "text-2xl font-semibold tracking-tight"
            }
          >
            {translate("marketing.title")}
          </h1>
          {isMobile ? null : (
            <SegmentedControl
              value={tab}
              options={options}
              onChange={onChange}
              label={label}
              fit
              className="w-[40rem] max-w-full"
            />
          )}
        </header>
        {isMobile ? (
          <div className="mi-tabs-sticky">
            <PillGroup
              label={label}
              value={tab}
              options={options}
              onChange={onChange}
            />
          </div>
        ) : null}
        <div className={isMobile ? "pt-1" : undefined}>
          {error ? (
            <p role="alert" className="py-10 text-center text-muted-foreground">
              {translate("marketing.common.error")}
            </p>
          ) : (
            <Routes>
              <Route index element={<SummaryView isPending={isPending} />} />
              <Route path="calendario" element={<CalendarView />} />
              <Route path="pipeline" element={<PipelineView />} />
              <Route
                path="planes"
                element={<PlansView isPending={isPending} />}
              />
              <Route
                path="planes/:id"
                element={<PlanDetail isPending={isPending} />}
              />
              <Route
                path="canales"
                element={<ChannelsView isPending={isPending} />}
              />
              <Route
                path="analiticas"
                element={<AnalyticsView isPending={isPending} />}
              />
              <Route
                path="*"
                element={<Navigate to={MARKETING_BASE} replace />}
              />
            </Routes>
          )}
        </div>
      </div>
      <PieceSheet
        open={!!pieceId && !isPending}
        piece={openedPiece}
        onClose={closePiece}
      />
      <PieceForm
        open={pieceForm.open}
        onOpenChange={(open) =>
          setPieceForm((current) => ({ ...current, open }))
        }
        piece={
          pieceForm.piece
            ? (findById(data.items, pieceForm.piece.id) ?? pieceForm.piece)
            : null
        }
        prefill={pieceForm.prefill}
        onDeleted={() => pieceId && closePiece()}
      />
      <MetricsForm
        open={metricsForm.open}
        onOpenChange={(open) =>
          setMetricsForm((current) => ({ ...current, open }))
        }
        target={metricsForm.target}
        row={metricsForm.row}
      />
    </MarketingContext.Provider>
  );
};

MarketingPage.path = `${MARKETING_BASE}/*`;
